import { defineStore } from 'pinia';
import * as SearchRepo from '@/repository/search';
import { useWebsiteStore } from './website';
import {
	CountryUtil,
	Menu,
	OfferField,
	OfferModel,
	OfferfieldModel,
	Request,
	SelectOption,
	Species,
	Constants,
	SeedFlags,
	MenuModel,
} from '@fibl/oxs-common';
import type { EAV } from '@fibl/zdb-common';
import { useRelatedStore } from './related';
import { Search } from '@fibl/zdb-common';
import { useSelectOptionStore } from './selectoption';
import { useMenuStore } from './menu';

type OfferCountAggregate = {
	groups?: {
		buckets: {
			key: string;
			doc_count: number;
			sort: {
				buckets: {
					key: string;
					doc_count: number;
				}[];
			};
		}[];
	};
};

type OfferFilter = {
	menu: EAV.Entity<Menu.Menu> | null;
	selectableSpeciesFilter: Search.SearchFilter;
	selectableSpeciesCount: number;
	activeSpecies: EAV.Entity<Species.Species>|null;
	baseFilters: {
		speciesOptionsFromMenu: string[];
		species: EAV.Entity<Species.Species>|null;
		sort: string;
		prmStatus: string;
		materialType: string;
		availabilityStatus: string;
		availabilityFrom: string;
		availabilityTo: string;
		breedingMethod: string;
		seedFlags: string;
		mixName: string;
	};
	dynamicFilterValues: Record<string, any>;
};

export type SearchMode = 'default' | 'request' | 'uware';

export const useOfferSearchStore = defineStore('offerSearch', {
	state: () => ({
		loading: true,
		mode: 'default' as SearchMode,
		seedFlagsHasOptions: true,
		sortHasOptions: true,
		filterDefault: {
			menu: null as EAV.Entity<Menu.Menu> | null,
			selectableSpeciesFilter: { $and: [] } as Search.SearchFilter,
			selectableSpeciesCount: 0,
			get activeSpecies() {
				return this.baseFilters.species;
			},
			baseFilters: {
				speciesOptionsFromMenu: [] as string[],
				species: null,
				sort: '',
				prmStatus: '',
				materialType: '',
				availabilityStatus: 'available',
				availabilityFrom: '',
				availabilityTo: '',
				historyFrom: '',
				historyTo: '',
				breedingMethod: '',
				seedFlags: '',
				mixName: '',
			},
			dynamicFilterValues: {} as Record<string, any>,
		},
		list: [] as OfferModel[],
		dynamicFilters: [] as OfferfieldModel[],
		offercounts: {} as OfferCountAggregate,
	}),

	getters: {
		cf(): OfferFilter {
			return this.filterDefault; //this.mode === 'default' ? this.filterDefault : this.filterRequest;
		},
		activeMaterialTypes(): string[] {
			if (!this.cf.activeSpecies) return [];
			return this.cf.activeSpecies.data.typeOfMaterialRestrictions;
		},
		menuSearchSettings(): Menu.Menu['search'] & {
			materialTypes: Menu.Menu['materialTypes'];
			availableRequestTypes: string[];
			category?: string[];
		} {
			if (!this.cf.menu) {
				return {
					availableRequestTypes: [],
					materialTypes: [],
					species: [],
				};
			}
			return Object.assign(
				{
					materialTypes: this.cf.menu.data.materialTypes || [],
					availableRequestTypes: this.cf.menu.data.availableRequestTypes || [],
					category: this.cf.menu.data.categoryFilter,
				},
				this.cf.menu.data.search,
			);
		},
		shownDynamicFields(): OfferfieldModel[] {
			if (!this.cf.activeSpecies) return [];
			return this.dynamicFilters.filter((f) => {
				// species filter
				if (f.species?.length) {
					if (!f.species.some((id) => id === this.cf.activeSpecies!.id)) return false;
				}
				if (!['checkbox', 'select', 'multiselect'].includes(f.type)) return false;
				// category filter
				const speciesOxsCats = this.cf.activeSpecies?.data.countrySpecific[this.country]?.categories || [];
				const speciesRdbCats = this.cf.activeSpecies!.data.rdbCategories || [];
				if (!speciesOxsCats.length && !speciesRdbCats.length) return false;
				if(f.oxsCat?.length && !f.oxsCat.some((cat)=> speciesOxsCats.includes(cat))) return false;
				if (f.rdbCat?.length && !f.rdbCat.some((cat) => speciesRdbCats.includes(cat))) return false;
				// material-type filter (menu-based, multiple, no explicit selection)
				const menuMatTypes = this.cf.menu?.data.materialTypes || [];
				if(f.typeOfMaterials?.length && !f.typeOfMaterials?.some((mat)=> menuMatTypes.includes(mat))) return false;
				//only relevant for country is loaded
				return true;
			});
		},
		country() {
			return useWebsiteStore().countryCode;
		},
		offerCountsTotal(): number {
			return (
				this.offercounts.groups?.buckets
					.map((e) => e.doc_count)
					.reduce((c, v) => c + v, 0) || 0
			);
		},
		offerCountForGroup() {
			return (group: string): number => {
				return (
					this.offercounts.groups?.buckets.find((e) => e.key === group)?.doc_count || 0
				);
			};
		},
		offerCountForSort() {
			return (group: string, sort: string): number => {
				return (
					this.offercounts.groups?.buckets
						.find((e) => e.key === group)
						?.sort.buckets.find((e) => e.key === sort)?.doc_count || 0
				);
			};
		},
		sortResultIds() {
			return (group: string): string[] => {
				return (
					this.offercounts.groups?.buckets
						.find((e) => e.key === group)
						?.sort.buckets.map((e) => e.key) || []
				);
			};
		},
		mixMode(): boolean {
			return !!this.menuSearchSettings.mixMode;
		},
		materialOptions(): EAV.Entity<SelectOption.SelectOption>[] {
			let base = useSelectOptionStore().byType('typeofmaterial');
			let filterSpecies = this.cf.activeSpecies?.data.typeOfMaterialRestrictions || [];
			let filterMenu = this.menuSearchSettings.materialTypes || [];
			return base.filter((e) => {
				if (filterSpecies.length && !filterSpecies.includes(e.id)) return false;
				if (filterMenu.length && !filterMenu.includes(e.id)) return false;
				return true;
			});
		},
		menusAvailableForRequest() {
			if (!this.cf.menu?.id) return [];
			const menuStore = useMenuStore();
			let allMenus = menuStore.getChildrenRecursive(this.cf.menu!.id);
			const country = useWebsiteStore().countryCode;
			allMenus.unshift(menuStore.byId(this.cf.menu!.id)!);
			allMenus = allMenus.filter(Boolean);
			if (allMenus.length > 1 && !this.cf.activeSpecies?.id) return [];
			// console.log({ allMenus });
			const requestTypeIdsForOrnamental = useSelectOptionStore().byType('reqtype')
				.filter(e => e.data.attributes.some(a => a.key === 'requestType' && a.value === '"ornamental"'))
				.map(e => e.id);
			return allMenus.map((ent => {
				let m = new MenuModel();
				m.fromEntity(ent);
				return m;
			})).filter(menu => {
				if (!menu.categoryFilter?.length) {
					// console.log('no cat filter', menu);
					return false;
				}
				const isOrnamentalRequest = this.cf.selectableSpeciesCount === 1
					&& menu.availableRequestTypes?.some(id => requestTypeIdsForOrnamental.includes(id))
					&& this.cf.activeSpecies?.data.attributes.some(e => e.value === '"ornamental"');
				if (this.cf.activeSpecies
					&& !menu.doesSpeciesMatch(this.cf.activeSpecies!, country)
					&& !isOrnamentalRequest
				) {
					// console.log('does not match species', menu);
					return false;
				}
				return true;
			});
		},
		effectiveMaterialFilter(): string {
			const matOpts = this.materialOptions;
			let matFilterValue = '';
			if (matOpts.length > 1) {
				if (this.cf.baseFilters.materialType) {
					matFilterValue = this.cf.baseFilters.materialType;
				}
			} else if (matOpts.length === 1) {
				matFilterValue = matOpts[0].id;
			} else {
				matFilterValue = '-';
			}
			return matFilterValue;
		},
		isSpecialOrnamentalSpeciesSet(): boolean {
			let spec = this.cf.activeSpecies;
			if (!spec) return false;
			return spec.data.attributes.some(e => e.key === 'flag' && e.value === '"ornamental"');
		},
		hasAllRequestFiltersFilled(): boolean {
			return !!this.cf.activeSpecies && !!this.effectiveMaterialFilter;
		},
		searchFilter(): SearchRepo.SearchFilter {
			const filter: SearchRepo.SearchFilter = { $and: [] };
			const now = new Date().toJSON().substring(0, 10);
			if (this.cf.activeSpecies) {
				filter.$and.push({
					field: '/data/species',
					comparison: 'eq',
					value: this.cf.activeSpecies.id,
				});
			} else {
				filter.$and.push({
					field: '/data/species',
					comparison: 'eq',
					value: '-',
				});
			}
			filter.$and.push({
				//should always be here because sup/aut might otherwise see differences
				field: '/indexfields/oxsVisibleCountries',
				comparison: 'eq',
				value: this.country,
			});
			if (this.mixMode) {
				if (this.cf.baseFilters.mixName) {
					filter.$and.push({
						field: '/data/mixName',
						comparison: 'eq',
						use: 'ngram',
						value: this.cf.baseFilters.mixName,
					});
				}
			} else {
				if (this.cf.baseFilters.sort) {
					filter.$and.push({
						field: '/data/sort',
						comparison: 'eq',
						value: this.cf.baseFilters.sort,
					});
				}
			}
			const websiteStore = useWebsiteStore();
			const country = websiteStore.countryCode;
			filter.$and.push({
				field: '/data/countrySpecific/' + country,
				comparison: 'exists',
				value: 'status',
			});
			if (this.cf.baseFilters.availabilityStatus === 'available') {
				filter.$and.push({
					field: '/data/countrySpecific/' + country + '/status',
					comparison: 'eq',
					value: 'available',
				});
			} else if (this.cf.baseFilters.availabilityStatus === 'availableInFuture') {
				if (this.cf.baseFilters.availabilityFrom) {
					filter.$and.push({
						field: '/data/countrySpecific/' + country + '/availableFrom',
						comparison: 'gte',
						value: this.cf.baseFilters.availabilityFrom,
					});
				}
				if (this.cf.baseFilters.availabilityTo) {
					filter.$and.push({
						field: '/data/countrySpecific/' + country + '/availableFrom',
						comparison: 'lte',
						value: this.cf.baseFilters.availabilityTo,
					});
				}
			} else {
				if (this.cf.baseFilters.availabilityFrom || this.cf.baseFilters.availabilityTo) {
					const nested: SearchRepo.SearchFilter = { $and: [] };
					const or: SearchRepo.SearchFilter = { $or: [] };
					nested.$and.push({
						field: '/data/indexData/availableHistory/country',
						comparison: 'eq',
						value: country,
					});
					if (this.cf.baseFilters.availabilityFrom) {
						nested.$and.push({
							$or: [
								{
									field: '/data/indexData/availableHistory/until',
									comparison: 'gt',
									value: this.cf.baseFilters.availabilityFrom,
								},
								{
									$not: {
										field: '/data/indexData/availableHistory',
										comparison: 'exists',
										value: 'until',
									},
								},
							],
						});
					}
					if (this.cf.baseFilters.availabilityTo) {
						nested.$and.push({
							field: '/data/indexData/availableHistory/from',
							comparison: 'lte',
							value: this.cf.baseFilters.availabilityTo,
						});
						if (this.cf.baseFilters.availabilityTo > now) {
							or.$or.push({
								field: '/data/countrySpecific/' + country + '/availableFrom',
								comparison: 'lte',
								value: this.cf.baseFilters.availabilityTo,
							});
						}
					} else {
						or.$or.push({
							field: '/data/countrySpecific/' + country + '/availableFrom',
							comparison: 'gt',
							value: now,
						});
					}
					or.$or.push({
						$nested: {
							path: '/data/indexData/availableHistory',
							match: nested,
						}
					});
					filter.$and.push(or);
				}
				filter.$and.push({
					$not: {
						field: '/data/countrySpecific/' + country + '/status',
						comparison: 'eq',
						value: 'archived',
					},
				});
			}
			if (this.cf.baseFilters.breedingMethod) {
				const selectOptStore = useSelectOptionStore();
				const options = selectOptStore.byType('breedingmethod');
				const bioOpt = options.find((op) => op.data.flags.includes('bio-dynamic'));
				const organicOpt = options.find((op) => op.data.flags.includes('organic'));
				if (bioOpt && organicOpt && this.cf.baseFilters.breedingMethod === organicOpt.id) {
					filter.$and.push({
						$or: [
							{
								field: '/data/indexData/sortBreedingmethod',
								comparison: 'eq',
								value: bioOpt.id,
							},
							{
								field: '/data/indexData/sortBreedingmethod',
								comparison: 'eq',
								value: organicOpt.id,
							},
						],
					});
				} else
					filter.$and.push({
						field: '/data/indexData/sortBreedingmethod',
						comparison: 'eq',
						value: this.cf.baseFilters.breedingMethod,
					});
			}
			if (this.cf.baseFilters.prmStatus) {
				filter.$and.push({
					field: '/data/prmStatus',
					comparison: 'eq',
					value: this.cf.baseFilters.prmStatus,
				});
			}
			let matFilterValue = this.effectiveMaterialFilter;
			if (matFilterValue) {
				filter.$and.push({
					field: '/data/typeOfMaterial',
					comparison: 'eq',
					value: matFilterValue,
				});
			}
			for (let field of this.shownDynamicFields) {
				if (field.getId() in this.cf.dynamicFilterValues) {
					const path = OfferfieldModel.TYPE_TO_PATH_MAP[field.type];
					const value = this.cf.dynamicFilterValues[field.getId()];
					if (
						value === null ||
						typeof value === 'undefined' ||
						(Array.isArray(value) && !value.length)
					)
						continue;
					const valueFilter: Search.SearchFilter[] = (
						Array.isArray(value) ? value : [value]
					).map((v) => ({
						field: `/data/countrySpecific/${country}/dynamicFields/${path}`,
						comparison: 'eq',
						value: v,
					}));
					filter.$and.push({
						$nested: {
							path: '/data/countrySpecific/' + country + '/dynamicFields',
							match: {
								$and: [
									{
										field:
											'/data/countrySpecific/' +
											country +
											'/dynamicFields/field',
										comparison: 'eq',
										value: field.getId(),
									},
									{ $or: valueFilter },
								],
							},
						},
					});
				}
			}
			if (this.mode === 'request') {
				const requestFilter: SearchRepo.SearchFilter = { $or: [] };
				for (let offerfield of this.dynamicFilters) {
					if (offerfield.shouldFilterForRequest) {
						const type = offerfield.type;
						const findQuery: SearchRepo.SearchFilter = {
							field: `/data/countrySpecific/${country}/dynamicFields/field`,
							comparison: 'eq',
							value: offerfield.getId(),
						};
						const filterStructure = (
							filterOutQuery: SearchRepo.SearchFilter,
						): SearchRepo.SearchFilter => {
							return {
								$and: [findQuery, filterOutQuery],
							};
						};
						const selectQuery = (typeValue: string) => {
							const optForRequestFilter: SearchRepo.SearchFilter = { $or: [] };
							for (let opt of offerfield.options) {
								if (opt.filterForRequest) {
									optForRequestFilter.$or.push({
										field: `/data/countrySpecific/${country}/dynamicFields/${typeValue}`,
										comparison: 'eq',
										value: opt.id,
									});
								}
							}
							requestFilter.$or.push(filterStructure(optForRequestFilter));
						};
						switch (type) {
							case 'checkbox': {
								requestFilter.$or.push(
									filterStructure({
										field: `/data/countrySpecific/${country}/dynamicFields/booleanValue`,
										comparison: 'eq',
										value: true,
									}),
								);
								break;
							}
							case 'select': {
								selectQuery('selectValue');
								break;
							}
							case 'multiselect': {
								selectQuery('multiselectValue');
								break;
							}
						}
					}
				}
				filter.$and.push({
					$not: {
						$nested: {
							path: '/data/countrySpecific/' + country + '/dynamicFields',
							match: requestFilter,
						},
					},
				});
			}
			if (this.cf.baseFilters.seedFlags) {
				filter.$and.push({
					field: '/data/indexData/seedFlags',
					comparison: 'eq',
					value: this.cf.baseFilters.seedFlags,
				});
			}
			return filter;
		},
	},

	actions: {
		setCombinedMenu(menu: EAV.Entity<Menu.Menu>) {
			if (menu.id === this.cf.menu?.id) return false;
			this.cf.menu = useMenuStore().getCombinedMenuItem(menu);
			return true;
		},
		setSelectableSpeciesFilter(menu: EAV.Entity<Menu.Menu>, ornamentalOverride=false) {
			if (ornamentalOverride) {
				this.cf.selectableSpeciesFilter = {
					field: '/data/attributes/value',
					comparison: 'eq',
					value: '"ornamental"',
				};
				return true;
			}
			const speciesFilter = useMenuStore().getSpeciesFilter(menu, this.mode === 'request');
			let mixFilter: Search.SearchFilter = {
				field: '/data/attributes/key',
				comparison: 'eq',
				value: 'formix',
			};
			if (!menu.data.search.mixMode) {
				mixFilter = { $not: mixFilter };
			}
			let newFilter: Search.SearchFilter = {
				$and: [speciesFilter, mixFilter],
			};
			let oldFilter =	this.cf.selectableSpeciesFilter;
			if (JSON.stringify(newFilter) !== JSON.stringify(oldFilter)) {
				this.cf.selectableSpeciesFilter = newFilter;
				return true;
			}
			return false;
		},
		async initDynamicFilters() {
			if (!this.dynamicFilters.length) {
				const websiteStore = useWebsiteStore();
				const country = websiteStore.countryCode;
				const fields = await SearchRepo.findAll<OfferField.OfferField>(
					'offerfield',
					{
						$and: [
							{
								field: '/data/availableInCountries',
								comparison: 'eq',
								value: country,
							},
							{
								field: '/data/displayInOfferSearch',
								comparison: 'eq',
								value: country,
							},
						],
					},
					true,
				);
				this.dynamicFilters = fields.map((f) => {
					let m = new OfferfieldModel();
					m.fromEntity(f);
					return m;
				});
			}
		},
		async updateSpeciesCount() {
			if (!this.cf.menu) throw new Error('no menu set');
			const country = useWebsiteStore().countryCode;
			this.cf.selectableSpeciesCount = await SearchRepo.countEntries(
				'species',
				this.cf.selectableSpeciesFilter,
			);
			if (this.cf.selectableSpeciesCount === 1) {
				let res = await SearchRepo.search<Species.Species>('species', this.cf.selectableSpeciesFilter);
				if (res.length) {
					await this.setSpeciesFilter(res[0]);
				} else {
					console.error('expected 1 result', res, this.cf.selectableSpeciesFilter);
				}
			} else {
				if (this.cf.activeSpecies?.id) {
					let m = new MenuModel();
					m.fromEntity(this.cf.menu!);
					let matches = m.doesSpeciesMatch(this.cf.activeSpecies, country);
					if (!matches) {
						this.setSpeciesFilter(null);
					}
				}
				if (this.cf.selectableSpeciesCount === 0) {
					console.warn('invalid species config!', this.cf.selectableSpeciesFilter);
				}
			}
		},
		async initMode(m: SearchMode) {
			if (m === this.mode) return;
			this.mode = m;
			this.resetDynFilters();
		},
		async initFromRequest(id: string) {
			// console.log('init from request', id);
			let req = (await useRelatedStore().addEntity(id)) as EAV.Entity<Request.RequestProcess>;
			let menu: EAV.Entity<Menu.Menu> = {
				id: req.id,
				data: {
					labels: [],
					search: {
						species: [req.data.species.id],
						mixMode: false,
						showAvailableFilter: false,
						showBreedingMethodFilter: false,
						showHistoryFilter: false,
						showPrmStatusFilter: true,
						showSortFilter: true,
					},
					sorting: 0,
					categoryFilter: req.data.searchFilter.category,
					website: Constants.WEB_OXS,
					country: req.data.country,
					materialTypes: req.data.searchFilter.typeOfMaterial
						? [req.data.searchFilter.typeOfMaterial]
						: [],
					availableRequestTypes: [req.data.extType as any],
				},
				rev: 0,
				entityclass: '',
				attributeset: '',
				created: '',
				updated: '',
				attributerev: 0
			};
			useMenuStore().pseudoMenu = menu;
			this.setCombinedMenu(menu);
			this.setSelectableSpeciesFilter(menu);
			await this.updateSpeciesCount();
			await this.initDynamicFilters();
			this.resetDynFilters();
			await this.initMode('request');
			this.cf.baseFilters.prmStatus = req.data.searchFilter.prmStatus || '';
			this.cf.baseFilters.materialType = req.data.searchFilter.typeOfMaterial || '';
			if (req.data.sort && 'id' in req.data.sort) {
				this.cf.baseFilters.sort = req.data.sort.id;
			}
			return req;
		},
		async initFromSpecies(query: { species: string; date: string }) {
			// console.log('init from species', query);
			let country = useWebsiteStore().countryCode;
			let menu: EAV.Entity<Menu.Menu> = {
				id: query.species,
				data: {
					labels: [],
					search: {
						species: [query.species],
						mixMode: false,
						showAvailableFilter: false,
						showBreedingMethodFilter: false,
						showHistoryFilter: true,
						showPrmStatusFilter: true,
						showSortFilter: true,
					},
					sorting: 0,
					website: Constants.WEB_OXS,
					country,
					materialTypes: [],
				},
				rev: 0,
				entityclass: '',
				attributeset: '',
				created: '',
				updated: '',
				attributerev: 0
			};
			let menuChanged = this.setCombinedMenu(menu);
			this.setSelectableSpeciesFilter(menu);
			await this.updateSpeciesCount();
			await this.initDynamicFilters();
			this.resetDynFilters();
			await this.initMode('default');
			this.cf.baseFilters.prmStatus = '';
			this.cf.baseFilters.materialType = '';
			this.cf.baseFilters.availabilityFrom = query.date;
			this.cf.baseFilters.availabilityTo = query.date;
		},
		async checkSeedFlagOptions() {
			const sfFilter: SearchRepo.SearchFilter = { $and: [] };
			const filter: SearchRepo.SearchFilter = {
				$or: [
					{
						$not: {
							field: '/data',
							comparison: 'exists',
							value: 'species',
						},
					},
				],
			};
			if (this.cf.activeSpecies) {
				filter.$or.push({
					field: '/data/species',
					comparison: 'eq',
					value: this.cf.activeSpecies.id,
				});
			} else {
				this.seedFlagsHasOptions = false;
				return;
			}
			sfFilter.$and.push(filter);
			const countryFilter: SearchRepo.SearchFilter = {
				$or: [
					{
						$not: {
							field: '/data',
							comparison: 'exists',
							value: 'visibleInCountries'
						}
					},
					{
						field: '/data/visibleInCountries',
						comparison: 'eq',
						value: this.country
					}
				]
			}
			sfFilter.$and.push(countryFilter);
			const res: EAV.Entity<SeedFlags.SeedFlags>[] = await SearchRepo.search('seedFlags', sfFilter, { limit: 10 });
			this.seedFlagsHasOptions = res.length > 0;
		},
		async checkSortOptions() {
			const filter: SearchRepo.SearchFilter = {
				$and: [{ field: '/data/status', comparison: 'eq', value: 'accepted' }],
			};
			if (this.cf.activeSpecies) {
				filter.$and.push({
					field: '/data/species',
					comparison: 'eq',
					value: this.cf.activeSpecies.id,
				});
			} else {
				this.sortHasOptions = false;
				return;
			}
			const res = await SearchRepo.searchOnlyIds('sort', filter, { limit: 1 });
			this.sortHasOptions = res.length > 0;
		},
		async setSpeciesFilter(species: EAV.Entity<Species.Species>|null) {
			const speciesId = species?.id;
			if (!speciesId || speciesId !== this.cf.baseFilters.species?.id) {
				this.cf.baseFilters.sort = '';
				this.cf.baseFilters.mixName = '';
			}
			this.cf.baseFilters.species = species;
			if (!speciesId) {
				this.offercounts = {};
			}
			this.resetDynFilters(false);
			this.cf.baseFilters.materialType = '';
			// console.debug('setSpecies', speciesId, this.cf);
			await this.checkSeedFlagOptions();
			await this.checkSortOptions();
		},
		async updateList() {
			this.loading = true;
			let agg = await SearchRepo.aggregateRun({
				entityClass: 'offer',
				attributeFilter: this.searchFilter,
				aggregate: {
					key: 'groups',
					type: 'term',
					field: `/data/countrySpecific/${this.country}/groups`,
					sub: [
						{
							key: 'sort',
							type: 'term',
							field: '/data/sort',
						},
					],
				},
			});
			this.offercounts = agg.aggregations;
			this.loading = false;
		},
		resetDynFilters(inclPrm=true) {
			this.cf.baseFilters.availabilityFrom = '';
			this.cf.baseFilters.availabilityStatus = 'available';
			this.cf.baseFilters.breedingMethod = '';
			this.cf.baseFilters.availabilityTo = '';
			// this.cf.baseFilters.materialType = '';
			if (inclPrm) this.cf.baseFilters.prmStatus = '';
			this.cf.baseFilters.seedFlags = '';
			this.cf.dynamicFilterValues = {};
		},
		resetAllFilters() {
			this.filterDefault = {
				menu: null as EAV.Entity<Menu.Menu> | null,
				selectableSpeciesFilter: { $and: [] } as Search.SearchFilter,
				selectableSpeciesCount: 0,
				get activeSpecies() {
					return this.baseFilters.species;
				},
				baseFilters: {
					speciesOptionsFromMenu: [] as string[],
					species: null,
					sort: '',
					prmStatus: '',
					materialType: '',
					availabilityStatus: 'available',
					availabilityFrom: '',
					availabilityTo: '',
					historyFrom: '',
					historyTo: '',
					breedingMethod: '',
					seedFlags: '',
					mixName: '',
				},
				dynamicFilterValues: {} as Record<string, any>,
			};
		}
	},
});
